import { computed, watch } from 'vue';
import { createI18n } from 'vue-i18n';

export type Locale = 'es' | 'en';
const storageKey = 'integrado.locale';
// Each feature owns its catalog. Eager loading two small text catalogs avoids a
// network round trip when switching language, including offline/error states.
const catalogs = import.meta.glob<{ es: Record<string, string>; en: Record<string, string> }>('./messages/*.ts', { eager: true });
export const messages: Record<Locale, Record<string, string>> = { es: {}, en: {} };
for (const catalog of Object.values(catalogs)) {
  for (const language of ['es', 'en'] as const) Object.assign(messages[language], catalog[language]);
}
function readLocale(): Locale {
  try { return localStorage.getItem(storageKey) === 'en' ? 'en' : 'es'; } catch { return 'es'; }
}
export const i18n = createI18n({
  legacy: false, locale: readLocale(), fallbackLocale: 'es', messages,
  // Catalog keys are literal: a label and its child labels may share a prefix.
  messageResolver: (catalog, key) => (catalog as Record<string, string>)[key] ?? null,
});
export const locale = i18n.global.locale;
export const intlLocale = computed(() => locale.value === 'en' ? 'en-US' : 'es-PE');
export function t(key: string, params?: Record<string, string | number>): string {
  return params ? i18n.global.t(key, params) : i18n.global.t(key);
}
function applyLocale() {
  document.documentElement.lang = locale.value;
  document.querySelector('meta[name="description"]')?.setAttribute('content', t('shell.metaDescription'));
}
export function setLocale(value: string) {
  if (value !== 'es' && value !== 'en') return;
  locale.value = value;
  try { localStorage.setItem(storageKey, value); } catch { /* Keep the current session usable without storage. */ }
}
watch(locale, applyLocale, { immediate: true, flush: 'sync' });
window.addEventListener('storage', event => {
  if (event.key === storageKey || event.key === null) locale.value = readLocale();
});
const numberFormats = new Map<string, Intl.NumberFormat>();
const dateFormats = new Map<string, Intl.DateTimeFormat>();
export function formatNumber(value: number, options: Intl.NumberFormatOptions = {}) {
  const key = `${intlLocale.value}:${JSON.stringify(options)}`;
  if (!numberFormats.has(key)) { if (numberFormats.size > 80) numberFormats.clear(); numberFormats.set(key, new Intl.NumberFormat(intlLocale.value, options)); }
  return numberFormats.get(key)!.format(value);
}
export function formatDate(value: string | number | Date, options: Intl.DateTimeFormatOptions = {}) {
  // A date-only record is a calendar date, not a midnight-UTC timestamp in Lima.
  const date = value instanceof Date ? value : new Date(typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.valueOf())) return '—';
  const settings: Intl.DateTimeFormatOptions = Object.keys(options).length ? { timeZone: 'America/Lima', ...options } : { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Lima' };
  const key = `${intlLocale.value}:${JSON.stringify(settings)}`;
  if (!dateFormats.has(key)) { if (dateFormats.size > 80) dateFormats.clear(); dateFormats.set(key, new Intl.DateTimeFormat(intlLocale.value, settings)); }
  return dateFormats.get(key)!.format(date);
}
function months(style: 'long' | 'short') {
  return Array.from({ length: 12 }, (_, month) => {
    const name = new Intl.DateTimeFormat(intlLocale.value, { month: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2026, month, 15)));
    return name.charAt(0).toUpperCase() + name.slice(1);
  });
}
export const monthNames = computed(() => months('long'));
export const shortMonthNames = computed(() => months('short'));
